// Panel derecho: la vista previa del Anexo.
//
// Portado de src/AnexoPreview.js — misma tubería exacta: bytes → Blob → object
// URL → <iframe>, con revokeObjectURL en la limpieza. Lo que ve el operador es
// byte por byte el PDF que se va a generar; no es una maqueta HTML.
//
// Única diferencia: en vez del generador que carga sus propios datos desde la
// base, llama a renderAnexoPdf con la entrada que arma anexoToPdfInput.js
// desde el estado vivo del workspace.
//
// El regenerado va al mismo compás de 3 s del autoguardado: reconstruir el PDF
// en cada tecla sería un desperdicio y haría parpadear el iframe.

import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { renderAnexoPdf } from '../../utils/generateAnexoPDF';
import { buildAnexoPdfInput } from './anexoToPdfInput';

const DgaPreviewPanel = ({ anexo, cliente, perfil, packages, items, beat, colapsado, onToggle }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [construyendo, setConstruyendo] = useState(true);
  const datosRef = useRef({});
  datosRef.current = { anexo, cliente, perfil, packages, items };

  useEffect(() => {
    let cancelado = false;
    let urlCreada = null;

    const construir = async () => {
      setConstruyendo(true);
      setError(null);
      try {
        const entrada = buildAnexoPdfInput(datosRef.current);
        const bytes = await renderAnexoPdf(entrada, {
          drawTaxColumn: true,
          logLabel: `anexo:${datosRef.current.anexo?.id || ''}`,
        });
        if (cancelado) return;
        const blob = new Blob([bytes], { type: 'application/pdf' });
        urlCreada = URL.createObjectURL(blob);
        setPdfUrl(urlCreada);
      } catch (err) {
        if (cancelado) return;
        console.error('[Desarrollos/vista previa] error generando', err);
        setError(err.message || 'Error generando la vista previa.');
      } finally {
        if (!cancelado) setConstruyendo(false);
      }
    };

    construir();

    return () => {
      cancelado = true;
      if (urlCreada) URL.revokeObjectURL(urlCreada);
    };
    // `beat` avanza con el pulso de autoguardado; ese es el disparador.
  }, [beat]);

  // Colapsado el panel se reduce a un riel; el iframe sigue montado (solo
  // oculto) para que renderAnexoPdf siga corriendo en el pulso de 3 s y no haya
  // que reconstruir el PDF al volver a abrirlo.
  if (colapsado) {
    return (
      <div className="flex h-full min-h-0 w-8 flex-col items-center rounded-lg border border-slate-200 bg-[#f8fafc] py-2.5">
        <button
          type="button"
          onClick={() => onToggle(false)}
          aria-label="Mostrar la vista previa del Anexo"
          title="Mostrar la vista previa"
          className="rounded p-1 text-slate-400 hover:text-slate-900"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="hidden">
          {pdfUrl && <iframe title="Vista previa del Anexo" src={pdfUrl} />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 bg-[#f8fafc] px-4 py-2.5">
        <h3 className="text-sm font-bold text-slate-900">Vista previa del Anexo</h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">
            {construyendo ? 'Actualizando…' : 'Solo lectura'}
          </span>
          <button
            type="button"
            onClick={() => onToggle(true)}
            aria-label="Ocultar la vista previa del Anexo"
            title="Ocultar la vista previa"
            className="rounded p-1 text-slate-400 hover:text-slate-900"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error ? (
        <p className="p-6 text-center text-xs text-red-600">{error}</p>
      ) : !pdfUrl ? (
        <p className="p-6 text-center text-xs text-slate-400">Generando vista previa…</p>
      ) : (
        <iframe
          title="Vista previa del Anexo"
          src={pdfUrl}
          className="min-h-0 flex-1 border-0"
        />
      )}
    </div>
  );
};

export default DgaPreviewPanel;
